// Sorting functions that match backend logic
export const sortingFunctions = {
  // Default backend sorting: weight-based (Amazon relevance + domain availability)
  amazon_results: (results) => {
    const sortedResults = [...results].sort((a, b) => {
      const weightA = calculateWeight(a);
      const weightB = calculateWeight(b);
      return weightB - weightA; // Descending order (highest weight first)
    });
    return sortedResults;
  },
  
  // Sort by price (lowest ILS price first)
  price: (results) => {
    const sortedResults = [...results].sort((a, b) => {
      const priceA = getILSPrice(a);
      const priceB = getILSPrice(b);
      if (priceA === null && priceB === null) return 0;
      if (priceA === null) return 1; // Items without ILS price go to end
      if (priceB === null) return -1; // Items without ILS price go to end
      return priceA - priceB;
    });
    return sortedResults;
  },
  
  // Sort like Amazon (preserve original Amazon order, just by position weight)
  amazon: (results) => {
    const sortedResults = [...results].sort((a, b) => {
      // Use original Amazon index if available for most accurate sorting
      if (a.amazonIndex !== undefined && b.amazonIndex !== undefined) {
        return a.amazonIndex - b.amazonIndex; // Ascending order (0, 1, 2, ...)
      }
      
      // Fallback to position weight
      const positionWeightA = getPositionWeight(a);
      const positionWeightB = getPositionWeight(b);
      return positionWeightB - positionWeightA;
    });
    return sortedResults;
  },
  
  // Sort by number of available domains/locales (most results first)
  results: (results) => {
    const sortedResults = [...results].sort((a, b) => {
      const localesCountA = getLocalesCount(a);
      const localesCountB = getLocalesCount(b);
      return localesCountB - localesCountA;
    });
    return sortedResults;
  }
};

// Calculate weight like in backend: position weight + locales count
export const calculateWeight = (item) => {
  const positionWeight = getPositionWeight(item);
  const localesCount = getLocalesCount(item);
  return positionWeight + localesCount;
};

// Get position weight (simulates backend logic where first items get weight 10, then 9, 8, etc.)
export const getPositionWeight = (item) => {
  // Use the original Amazon index if available (should be provided by backend)
  if (item.amazonIndex !== undefined && item.amazonIndex !== null) {
    // Convert Amazon index (0-based) to weight (higher weight for earlier positions)
    // First item (index 0) gets weight 10, second (index 1) gets 9, etc.
    return Math.max(10 - item.amazonIndex, 1); // Ensure minimum weight of 1
  }
  
  // If item has a legacy weight property, use it
  if (item.weight) return item.weight;
  
  // Fallback: estimate based on some identifier or default to medium weight
  console.warn('Item missing amazonIndex property:', item);
  return 5; // Default medium weight if no position info available
};

// Count available locales/domains
export const getLocalesCount = (item) => {
  if (item.locales && typeof item.locales === 'object') {
    return Object.keys(item.locales).length;
  }
  return 1; // Default to 1 if no locales info
};

// Get lowest ILS price from all locales
export const getILSPrice = (item) => {
  if (!item.locales) return null;
  
  let lowestILSPrice = null;
  Object.values(item.locales).forEach(locale => {
    const priceILS = locale.priceIls;
    if (priceILS && !isNaN(priceILS)) {
      if (lowestILSPrice === null || priceILS < lowestILSPrice) {
        lowestILSPrice = priceILS;
      }
    }
  });
  
  return lowestILSPrice;
};

// Apply sorting to results
export const applySorting = (results, sortOption) => {
  if (!results || !Array.isArray(results)) return results;
  
  const sortFunction = sortingFunctions[sortOption];
  if (!sortFunction) return results;
  
  return sortFunction(results);
}; 